import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import AuthLayout from "../components/layout/AuthLayout";
import PasswordInput from "../components/PasswordInput";
import { OTPInput, Input, Button } from "../components/ui";
import SEO from "../components/SEO";

const Signup = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const { signup, resendSignupOTP } = useAuth();

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOTP = async () => {
    try {
      await resendSignupOTP(formik.values.email);
      setResendTimer(60);
      setCanResend(false);
      toast.success("OTP resent to your email");
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    }
  };

  const getSignupSchema = (isOtpSent) =>
    Yup.object().shape({
      name: isOtpSent
        ? Yup.string()
        : Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: isOtpSent
        ? Yup.string()
        : Yup.string()
            .min(8, "Password must be at least 8 characters")
            .required("Password is required"),
      confirmPassword: isOtpSent
        ? Yup.string()
        : Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm password is required"),
      otp: isOtpSent ? Yup.string().required("OTP is required") : Yup.string(),
    });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      otp: "",
      isOtpSent: false,
    },
    validationSchema: getSignupSchema(isOtpSent),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!isOtpSent) {
          await signup(values.name, values.email, values.password);
          setIsOtpSent(true);
          setResendTimer(60);
          setCanResend(false);
          toast.success("OTP sent to your email");
        } else {
          await signup(values.name, values.email, values.password, values.otp);
          toast.success("Account created successfully");
          navigate("/login");
        }
      } catch (error) {
        formik.setFieldError(
          "formError",
          error?.response?.data?.message ||
            error?.message ||
            (isOtpSent ? "Failed to verify email" : "Failed to create account"),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <SEO index="signup" />
      <div className="space-y-8">
        <div>
          <h2 className="font-heading text-4xl font-bold text-gray-900">
            Create an account
          </h2>
          <p className="text-sm text-gray-500 mt-3">
            Enter your details to get started.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            formik.handleSubmit(e);
            e.preventDefault();
          }}
          className="space-y-5"
        >
          <Input
            id="name"
            name="name"
            type="text"
            label="Full name"
            required
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="John Doe"
            disabled={isOtpSent}
            error={
              formik.errors.name && formik.touched.name
                ? formik.errors.name
                : undefined
            }
          />

          <Input
            id="email"
            name="email"
            type="email"
            label="Email"
            required
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="mail@example.com"
            disabled={isOtpSent}
            error={
              formik.errors.email && formik.touched.email
                ? formik.errors.email
                : undefined
            }
          />

          {!isOtpSent && (
            <>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  placeholder="Min. 8 characters"
                  error={
                    formik.errors.password && formik.touched.password
                      ? formik.errors.password
                      : undefined
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                  className={`w-full px-4 py-3 pr-10 text-sm font-medium border rounded-lg placeholder-gray-500 text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    formik.errors.confirmPassword &&
                    formik.touched.confirmPassword
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300"
                  }`}
                  placeholder="Confirm your password"
                  error={
                    formik.errors.confirmPassword &&
                    formik.touched.confirmPassword
                      ? formik.errors.confirmPassword
                      : undefined
                  }
                />
              </div>
            </>
          )}

          {isOtpSent && (
            <div className="animate-fadeIn">
              <OTPInput
                name="otp"
                value={formik.values.otp}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.errors.otp && formik.touched.otp
                    ? formik.errors.otp
                    : undefined
                }
                resendTimer={resendTimer}
                onResend={handleResendOTP}
              />
            </div>
          )}

          {formik.errors.formError && (
            <div className="text-red-500 text-xs bg-red-50 border border-red-200 p-3 rounded-xl flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 shrink-0" />
              <span>{formik.errors.formError}</span>
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
            className="py-3"
          >
            {isOtpSent ? "Verify Email" : "Create account"}
          </Button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-primary hover:text-primary-dark"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Signup;
